function parseEnum(_enum: any) {
  const allValues: (string | number)[] = Object.values(_enum);
  const properties: string[] = allValues.slice(
    0,
    allValues.length / 2,
  ) as string[];
  const values: (string | number)[] = allValues.slice(allValues.length / 2);
  const enumObj: Record<string, number | string> = {};

  properties.forEach((prop, index) => {
    enumObj[prop] = values[index];
  });

  return enumObj;
}

function getEnumNamesAndValues<T extends number | string>(_enum: any) {
  const obj = parseEnum(_enum);
  return Object.keys(obj).map((n) => ({ name: n, value: obj[n] as T }));
}

function getEnumNames(_enum: any) {
  return Object.keys(parseEnum(_enum));
}

function getEnumValues<T extends number | string>(_enum: any): T[] {
  return Object.values(parseEnum(_enum)) as T[];
}

function convertToEnum<T>(enumType: T, value: any) {
  return enumType[value as unknown as keyof typeof enumType];
}

function ensureEnumKey<T>(enumType: T, value: any) {
  if (typeof value === 'string') {
    return value as T;
  }

  return enumKey<T>(enumType, value);
}

function enumKey<T>(enumType: T, value: any) {
  return enumType[
    value as unknown as keyof typeof enumType
  ] as keyof typeof enumType;
}

function compareEnum<T>(_enum: any, value: T, enumValue: T) {
  if (typeof value === 'string') {
    if (typeof enumValue === 'string') {
      return value == enumValue;
    }

    return ensureEnumNumber(_enum, value) == enumValue;
  }

  return value == ensureEnumNumber(_enum, enumValue);
}

function compareEnums<T>(_enum: any, value: T, enumValues: T[]) {
  return enumValues.some((ev) => compareEnum(_enum, value, ev));
}

function ensureEnumNumber<T>(_enum: any, value: T): T {
  if (typeof value === 'string') {
    return convertToEnum(_enum, value);
  }

  return value;
}

function normalizeEnumNumber<T extends number>(
  enumType: Record<string | number, string | number>,
  value: T,
  fallback: T,
): T {
  if (typeof value === 'number') return value;

  const numericValue = Number(value);
  if (!Number.isNaN(numericValue)) return numericValue as T;

  const normalizedValue = ensureEnumNumber(enumType, value);
  return typeof normalizedValue === 'number'
    ? (normalizedValue as T)
    : fallback;
}

export {
  parseEnum,
  getEnumNamesAndValues,
  getEnumNames,
  getEnumValues,
  convertToEnum,
  enumKey,
  compareEnum,
  compareEnums,
  ensureEnumNumber,
  ensureEnumKey,
  normalizeEnumNumber,
};
